import { css, html, nothing } from "lit";
import { fallbackLabels, type LabelTable, type Labels } from "../../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressAudio, pressFeedback } from "../../chrome/ChromeElement";
import { artImage, artMask, type ChromeArt, type ChromeArtFrame } from "../../chrome/chromeArt";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import type { ShellMenuBindings, ShellMenuPort } from "./ShellMenuPorts";

export const SHELL_MENU_LABELS = {
  sound: ["menu.sound", "Sound"],
  music: ["menu.music", "Music"],
  vibration: ["menu.vibration", "Vibration"],
  history: ["menu.history", "Game History"],
  info: ["menu.info", "Info"],
  home: ["menu.home", "Home"],
} as const satisfies LabelTable;
export type ShellMenuLabels = Labels<typeof SHELL_MENU_LABELS>;

/** The `ManualGridLayout` on `GridRoot`: 2 columns, rows 400×100, spacing / padding 10, the panel = grid + 40; rows fill from the BOTTOM. */
const GRID = { columns: 2, rowHeight: 100, spacing: 10, padding: 10, containerWidth: 811.9, extra: 40 } as const;
const PANEL = { width: GRID.containerWidth + GRID.extra, desktopMaxWidth: 851.9, mobileMaxWidth: 1920, mobileSlotScale: 0.907, closeSize: 87.49, closeBelow: 104.4, panelAlpha: 200 / 255, backdropAlpha: 221 / 255 } as const;
/** Each row's icon (frame, size, left margin) and label left margin, as authored. */
const ROWS = {
  sound: { frame: "volume", width: 29.73, height: 35, left: 27.42, textLeft: 109.8, textY: 25.45 },
  music: { frame: "musicIcon", width: 40, height: 44.05, left: 30.41, textLeft: 90 },
  vibration: { frame: "vibrationIcon", width: 53.33, height: 47.84, left: 27.31, textLeft: 90 },
  history: { frame: "historyIcon", width: 40, height: 44.05, left: 30.41, textLeft: 90 },
  info: { frame: "infoIcon", width: 24.52, height: 45.72, left: 39.07, textLeft: 90 },
  home: { frame: "homeIcon", width: 45.29, height: 36.01, left: 30.71, textLeft: 90 },
} as const satisfies Record<string, { frame: ChromeArtFrame; width: number; height: number; left: number; textLeft: number; textY?: number }>;
const SLIDER = { x: 35.29, yDown: 15.82, track: 250, height: 20, handle: 40 } as const;

export const SHELL_MENU_TAG = "slot-shell-menu";

/**
 * `<slot-shell-menu>`: the settings menu. Desktop: a popover whose bottom-left corner sits at the
 * bar's menu slot (the bar positions the host), closed by a press outside it. Mobile: a dimmed backdrop
 * (a tap on it closes the menu), the panel centred at the slot's 0.907 scale, a close button below. The
 * rows are the grid the prefab authored: sound (icon button + volume slider), music, vibration
 * (mobile), history, info, home.
 */
export class ShellMenuElement extends ChromeElement implements ShellMenuPort {
  public static override properties = {
    ...ChromeElement.properties,
    art: { attribute: false },
    labels: { attribute: false },
    open: { type: Boolean, reflect: true },
    soundOn: { state: true },
    volume: { state: true },
    musicOn: { state: true },
    musicEnabled: { state: true },
    vibrationOn: { state: true },
    historyShown: { state: true },
    historyEnabled: { state: true },
    infoEnabled: { state: true },
  };

  public declare art: ChromeArt;
  public declare labels: ShellMenuLabels;
  public declare open: boolean;
  /** Desktop: the toggle that opened the popover, excluded from outside-click dismissal. */
  public anchor: EventTarget | null = null;
  private declare soundOn: boolean;
  private declare volume: number;
  private declare musicOn: boolean;
  private declare musicEnabled: boolean;
  private declare vibrationOn: boolean;
  private declare historyShown: boolean;
  private declare historyEnabled: boolean;
  private declare infoEnabled: boolean;
  private bindings: ShellMenuBindings | null = null;

  public constructor() {
    super();
    this.art = {};
    this.labels = fallbackLabels(SHELL_MENU_LABELS);
    this.open = false;
    this.soundOn = true;
    this.volume = 1;
    this.musicOn = true;
    this.musicEnabled = true;
    this.vibrationOn = true;
    this.historyShown = true;
    this.historyEnabled = true;
    this.infoEnabled = true;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: none; color: #fff; font-weight: 500; font-size: 25px; }
      :host([open]) { display: block; }
      /* Mobile: a dimmed layer over the whole screen, the bar and its toggle included (the prefab's backdrop, black at 221/255); a tap on it closes the menu. */
      .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${Number(PANEL.backdropAlpha.toFixed(3))}); pointer-events: auto; }
      .panel { position: absolute; width: ${PANEL.width}px; border-radius: 8px; background: rgba(0, 0, 0, ${Number(PANEL.panelAlpha.toFixed(3))}); pointer-events: auto; }
      .grid { position: absolute; left: ${GRID.padding + GRID.extra / 2}px; right: ${GRID.padding + GRID.extra / 2}px; top: ${GRID.padding + GRID.extra / 2}px; bottom: ${GRID.padding + GRID.extra / 2}px; display: grid; grid-template-columns: repeat(${GRID.columns}, 1fr); gap: ${GRID.spacing}px; align-content: end; }
      .row { position: relative; height: ${GRID.rowHeight}px; border-radius: 8px; background: #262626; text-align: left; overflow: visible; }
      .row[hidden] { display: none; }
      .row .icon { position: absolute; top: calc(50% - 0.59px); transform: translate(0, -50%); }
      .row .off { position: absolute; width: 67.65px; height: 5.067px; background: #f86363; left: 50%; top: 50%; transform-origin: center; }
      .row .label { position: absolute; top: 50%; transform: translateY(-50%); white-space: nowrap; }
      .row:hover .icon, .row:hover .label { filter: brightness(0.86); }
      .row:active .icon, .row:active .label { filter: brightness(0.7); }
      .row[disabled] .icon, .row[disabled] .label { filter: brightness(0.5); }
      .sound-button { position: absolute; left: 0; top: 0; width: 90.71px; height: 100%; }
      .slider { position: absolute; top: calc(50% + ${SLIDER.yDown}px); left: calc(50% + ${SLIDER.x}px); width: ${SLIDER.track}px; height: 44px; transform: translate(-50%, -50%); margin: 0; -webkit-appearance: none; appearance: none; background: transparent; cursor: pointer; }
      .slider::-webkit-slider-runnable-track { height: ${SLIDER.height}px; border-radius: 10px; background: linear-gradient(to right, #89bb5b 0 var(--fill), rgba(255, 255, 255, 0.34) var(--fill) 100%); }
      .slider::-webkit-slider-thumb { -webkit-appearance: none; width: ${SLIDER.handle}px; height: ${SLIDER.handle}px; margin-top: ${-(SLIDER.handle - SLIDER.height) / 2}px; border-radius: 50%; background: #fff; }
      .slider::-moz-range-track { height: ${SLIDER.height}px; border-radius: 10px; background: rgba(255, 255, 255, 0.34); }
      .slider::-moz-range-progress { height: ${SLIDER.height}px; border-radius: 10px; background: #89bb5b; }
      .slider::-moz-range-thumb { width: ${SLIDER.handle}px; height: ${SLIDER.handle}px; border: 0; border-radius: 50%; background: #fff; }
      .close { position: absolute; left: 50%; width: ${PANEL.closeSize}px; height: ${PANEL.closeSize}px; transform: translateX(-50%); }
      .close:hover { filter: brightness(0.83); }
    `,
  ];

  public bind(bindings: ShellMenuBindings): void {
    this.bindings = bindings;
  }

  public setMenuOpen(open: boolean): void {
    this.open = open;
  }

  public setSoundEnabled(enabled: boolean): void {
    this.soundOn = enabled;
  }

  public setMasterVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  public setMusicEnabled(enabled: boolean): void {
    this.musicOn = enabled;
  }

  public setVibrationEnabled(enabled: boolean): void {
    this.vibrationOn = enabled;
  }

  public setHistoryButtonVisible(visible: boolean): void {
    this.historyShown = visible;
  }

  public setHistoryButtonInteractable(interactable: boolean): void {
    this.historyEnabled = interactable;
  }

  public setMusicInteractable(interactable: boolean): void {
    this.musicEnabled = interactable;
  }

  public setInfoButtonInteractable(interactable: boolean): void {
    this.infoEnabled = interactable;
  }

  protected override updated(): void {
    this.syncOutsideClick(this.open, this.anchor, () => this.bindings?.onOutsideClick());
  }

  /**
   * The rows as the grid renders them. The prefab's grid filled from the BOTTOM — sound and music
   * on the bottom line, then vibration (mobile) and history, then info and home — so the fill order
   * is chunked into lines and the lines reversed for the top-down DOM grid. An odd row count leaves
   * the LAST fill line short; it becomes the top line, so it is padded with empty cells to keep the
   * rows below it in their authored columns.
   */
  private rows(): unknown[] {
    const mobile = !this.isDesktop;
    const fillOrder = [
      this.soundRow(),
      this.row("music", this.labels.music, { on: this.musicOn, offLine: 45 }, this.musicEnabled, () => this.bindings?.onMusic()),
      mobile ? this.row("vibration", this.labels.vibration, { on: this.vibrationOn, offLine: -45 }, true, () => this.bindings?.onVibration()) : null,
      this.historyShown ? this.row("history", this.labels.history, null, this.historyEnabled, () => this.bindings?.onHistory()) : null,
      this.row("info", this.labels.info, null, this.infoEnabled, () => this.bindings?.onInfo()),
      this.row("home", this.labels.home, null, true, () => this.bindings?.onHome()),
    ].filter((row) => row !== null);
    const lines: unknown[][] = [];
    for (let i = 0; i < fillOrder.length; i += GRID.columns) lines.push(fillOrder.slice(i, i + GRID.columns));
    const shortLine = lines[lines.length - 1];
    if (shortLine) while (shortLine.length < GRID.columns) shortLine.push(html`<div></div>`);
    return lines.reverse().flat();
  }

  private icon(spec: { frame: ChromeArtFrame; width: number; height: number; left: number }, state: { on: boolean; offLine: number } | null) {
    return html`<div class="icon mask" style="left: ${spec.left}px; width: ${spec.width}px; height: ${spec.height}px; ${artMask(this.art, spec.frame, "#ffffff")}"></div>
      ${state && !state.on ? html`<div class="off" style="left: ${spec.left + spec.width / 2}px; transform: translate(-50%, -50%) rotate(${-state.offLine}deg)"></div>` : nothing}`;
  }

  private row(key: keyof typeof ROWS, label: string, state: { on: boolean; offLine: number } | null, enabled: boolean, onTap: () => void) {
    const spec = ROWS[key];
    return html`<button class="row" type="button" data-part=${key} ?disabled=${!enabled} @pointerdown=${pressFeedback} @click=${onTap}>
      ${this.icon(spec, state)}
      <div class="label" style="left: ${spec.textLeft}px">${label}</div>
    </button>`;
  }

  private soundRow() {
    const spec = ROWS.sound;
    return html`<div class="row" data-part="sound">
      <button class="sound-button" type="button" data-part="sound-toggle" @pointerdown=${pressFeedback} @click=${() => this.bindings?.onSound()}>
        ${this.icon(spec, { on: this.soundOn, offLine: 45 })}
        ${this.soundOn ? html`<div class="icon mask" style="left: ${spec.left + spec.width / 2 + 25.39 - 9.03}px; width: 18.06px; height: 35.27px; ${artMask(this.art, "volumeLines", "#ffffff")}"></div>` : nothing}
      </button>
      <div class="label" style="left: ${spec.textLeft}px; top: calc(50% - ${spec.textY}px)">${this.labels.sound}</div>
      <input class="slider" data-part="volume" type="range" min="0" max="1" step="0.01" .value=${String(this.volume)} style="--fill: ${this.volume * 100}%" @input=${(e: Event) => this.bindings?.onMasterVolumeChange(Number((e.target as HTMLInputElement).value))} />
    </div>`;
  }

  /** The grid's height for the active rows: rows of 100 with 10 between, 10 padding, plus the panel's 40. */
  private panelHeight(rowCount: number): number {
    const lines = Math.ceil(rowCount / GRID.columns);
    return lines * GRID.rowHeight + (lines - 1) * GRID.spacing + 2 * GRID.padding + GRID.extra;
  }

  protected override render() {
    const rows = this.rows();
    const height = this.panelHeight(rows.length);
    if (this.isDesktop) {
      // Bottom-left anchored at the host origin (the bar positions the element there); fitted to the slot's 851.6×540.1 area.
      const scale = Math.min(1, PANEL.desktopMaxWidth / PANEL.width, 851.6 / PANEL.width, 540.1 / height);
      return html`<div class="panel" data-part="panel" style="left: 0; bottom: 0; height: ${height}px; transform: scale(${scale}); transform-origin: bottom left"><div class="grid">${rows}</div></div>`;
    }
    const v = this.view.design;
    const area = { width: v.width / PANEL.mobileSlotScale, height: v.height / PANEL.mobileSlotScale };
    const scale = Math.min(PANEL.mobileMaxWidth / PANEL.width, area.width / PANEL.width, area.height / height) * PANEL.mobileSlotScale;
    return html`
      <div class="backdrop" data-part="backdrop" @pointerdown=${pressAudio} @click=${() => this.bindings?.onBackdrop()}></div>
      <div class="panel" data-part="panel" style="left: 50%; top: 50%; height: ${height}px; transform: translate(-50%, -50%) scale(${scale})">
        <div class="grid">${rows}</div>
        <button class="close art" type="button" data-part="close" style="top: ${height + PANEL.closeBelow - PANEL.closeSize}px; ${artImage(this.art, "close")}" @pointerdown=${pressFeedback} @click=${() => this.bindings?.onClose()}></button>
      </div>
    `;
  }
}

export function defineShellMenuElement(): void {
  defineChromeElement(SHELL_MENU_TAG, ShellMenuElement);
}
